/**
 * The engine handoff (HIG-5971): hands a prepared turn to `submitTurn`,
 * observes the SSE stream for the UI side effects the fold can't own, and
 * funnels every failed turn (mid-stream or thrown) through one path that
 * records the typed error, reports telemetry, and stamps the transcript.
 * Split out of `submit-flow.ts` as the self-contained "hand the turn to the
 * engine and apply its side effects" concern; `submit-flow.ts` still owns
 * drafting the turn and its pre-engine stages.
 */
import { GlobalEvents } from '@highlight/ipc'
import type { ChatAttachment, FinalizedAttachment, MentionableItem } from '../types'
import type { components as BackendOpenApiComponents } from '../types/backend-openapi'
import { isBuiltinTool } from '../integrations/utils'
import { initialTurnState } from '../engine/accumulator'
import { RAY_ID_UNAVAILABLE, describeTurnError, isRetryable, type ChatTurnError } from '../engine/errors'
import { isSubagentScoped, type ChatSSEEvent } from '../engine/events'
import type { ChatRequestBody } from '../engine/services'
import { submitTurn } from '../engine/submit'
import { adoptTurnKey, startTurn } from '../engine/turn-registry'
import { migrateResumeCursor, setResumeCursor } from './resume-cursors'
import type { SubmitFlowDeps, SubmissionState, TurnDraft } from './submit-flow'
import type { TurnPresentation } from './submit-presentation'
import { trackRagUsed } from './submit-telemetry'
import type { AutoToolTracker } from './use-auto-tool-responses'

type TurnFailureContext = {
  chatId: string
  // false when the failing submission errored before its turn landed in the
  // transcript (queued message pre-`metadata`) — the usual removeLastMessage/
  // updateLastMessage mutations would target the previous turn instead.
  wasTurnCommitted: boolean
  /**
   * Overrides for the failure stamp on the user message. The attachment-upload
   * path pins the exact optimistic id and restores the parsed mentions; engine
   * failures use the role-fallback match (the id may have been renamed by
   * `metadata`) and clear mentions. `omit` skips the stamp entirely — a
   * promptless submission's failure has no user bubble of its own.
   */
  userStamp?: { id?: string; mentions?: Array<MentionableItem>; omit?: boolean }
}

// Every failed turn lands here: records the typed error and stamps the
// transcript. Reporting is the failure site's job (the engine for its own
// turns, the facade for pre-engine failures), so a report never trails its
// turn's settle.
export function handleTurnFailure(deps: SubmitFlowDeps, error: ChatTurnError, context: TurnFailureContext): void {
  // Key the turn error by the conversation id current at failure time — the
  // engine context id predates a temp→real rename.
  const turnError: ChatTurnError = error.chatId === context.chatId ? error : { ...error, chatId: context.chatId }
  deps.store.setTurnError(turnError)

  // The server owns the 2.0 onboarding record. Only the flow itself can change its answer.
  if (turnError._tag === 'OnboardingRequired') {
    deps.ui.restartOnboarding()
  }

  // Ensure the last blank assistant message is removed whenever the turn had
  // already committed to the transcript — true for both the SIGNUP_REQUIRED
  // short-circuit below and the general failure path that follows.
  if (context.wasTurnCommitted) {
    deps.store.removeLastMessage(turnError.chatId, { role: 'assistant' })
  }

  const errorCode = turnError._tag === 'BackendTurnError' ? turnError.errorCode : null
  if (errorCode === 'SIGNUP_REQUIRED') return

  const rayId = 'rayId' in turnError ? turnError.rayId : undefined
  if (!context.wasTurnCommitted) return

  // Stamp the failure onto the user message. The default omits `id` so the
  // fallback role match applies without overwriting the message's real id —
  // the Retry button matches that id against the pending message.
  if (context.userStamp?.omit) return
  deps.store.updateLastMessage(turnError.chatId, {
    role: 'user',
    ...(context.userStamp?.id !== undefined ? { id: context.userStamp.id } : {}),
    mentions: context.userStamp?.mentions ?? [],
    error: {
      kind: turnError._tag,
      canRetry: isRetryable(turnError),
      message: describeTurnError(turnError),
      rayId: rayId ?? RAY_ID_UNAVAILABLE,
    },
  })
}

/**
 * The engine drains the stream and applies all store patches; this observer
 * handles only the UI side effects the fold can't own. `registryKey` is
 * shared with `runEngineTurn`'s startTurn call, which is why it travels as a
 * cell rather than a value.
 */
function createTurnObserver(
  deps: SubmitFlowDeps,
  draft: TurnDraft,
  turn: TurnPresentation,
  state: SubmissionState,
  autoTools: AutoToolTracker,
  registryKey: { current: string },
  abortTurn: { current: (() => void) | null },
  onTurnAccepted: (() => void) | undefined,
): (event: ChatSSEEvent) => void {
  let typingPromise: Promise<void> | undefined
  return (event: ChatSSEEvent) => {
    if (isSubagentScoped(event)) return
    switch (event.type) {
      case 'queued':
        turn.onQueued(event.queuedId)
        break
      case 'metadata': {
        // Drain the sink before this handler's direct store writes: the
        // temp→real migration below moves the transcript, and buffered
        // patches still keyed to the temp id must land before it runs.
        deps.engine.sink.flush()
        // Queue-path: drop the bubble and run the deferred turn-start.
        turn.onMetadata()
        // Still uncommitted after `metadata` means the queued bubble was
        // cancelled — the backend started the turn before the DELETE landed.
        // Abort before the engine folds this event (or any later ones), so
        // the orphaned stream can't write into the chat that is streaming.
        if (!turn.committed) {
          abortTurn.current?.()
          break
        }
        onTurnAccepted?.()
        // Next turn is starting — chat is no longer waiting for a drain.
        deps.queue.clearAwaitingNextDrain(draft.conversationId)
        // This stream now owns the chat — migrate a queued side key onto
        // the chat key so later turns and subscribes supersede it instead
        // of double-writing.
        if (turn.committed && registryKey.current !== draft.conversationId) {
          adoptTurnKey(registryKey.current, draft.conversationId)
          registryKey.current = draft.conversationId
        }
        // Temp→real id migration for new chats. Runs before the engine's
        // fold patches for this event, which already target the real id.
        const backendConversationId = event.conversationId
        if (backendConversationId) {
          draft.backendConversationId = backendConversationId
          // A temp id resent as an existing chat (a retry after a pre-metadata
          // failure) comes back unchanged, so the rename below never confirms it.
          deps.store.confirmServerChatId(backendConversationId)
        }
        if (backendConversationId && !draft.isExisting && backendConversationId !== draft.conversationId) {
          deps.store.updateMessagesChatId(draft.conversationId, backendConversationId)
          // Persist temp ID → real ID mapping for recovery (HIG-4293)
          deps.data.addChatIdMapping(draft.conversationId, backendConversationId)
          migrateResumeCursor(draft.conversationId, backendConversationId)
          deps.data.invalidateHistoryPreview()
          deps.data.invalidateChatHistory()
          // Only update the active chat if the user is still viewing THIS
          // stream's chat — if they backed out or switched, don't override.
          deps.store.setActiveChatId((prev) => (prev === draft.conversationId ? backendConversationId : prev))
          deps.engine.streaming.updateChatId(draft.conversationId, backendConversationId)
          adoptTurnKey(registryKey.current, backendConversationId)
          registryKey.current = backendConversationId
          state.streamingChatId = backendConversationId
          draft.conversationId = backendConversationId
          // Notify history so the chat appears immediately in the sidebar
          GlobalEvents.emit('chat:created', { chatId: backendConversationId })
        }
        // Keep the retry handle in sync with the renamed user message so a
        // later failure can still surface the Retry button.
        if (event.lastUserMessageId) {
          const backendUserMessageId = event.lastUserMessageId
          deps.store.setPendingMessage((prev) =>
            prev && prev.id === draft.pendingMessageId ? { ...prev, id: backendUserMessageId } : prev,
          )
          draft.pendingMessageId = backendUserMessageId
        }
        // The backend started the turn — this chat's stream is delivering.
        deps.engine.streaming.markStreaming(draft.conversationId)
        break
      }
      case 'text': {
        const streamEvent = event as BackendOpenApiComponents['schemas']['ChatSSEStreamEvent']
        if (typingPromise) {
          typingPromise = typingPromise.then(() => deps.actions.onChunkParsed?.(streamEvent, draft.options))
        } else {
          typingPromise = deps.actions.onChunkParsed?.(streamEvent, draft.options) ?? undefined
        }
        if (!draft.options?.preserveComposerContext) {
          deps.ui.resetMentions()
        }
        break
      }
      case 'toolUse':
        // A client tool the frontend can't run — the fold marks it failed;
        // post the failure result back so the model can continue.
        if (!isBuiltinTool(event.name) && event.willSendToolResponse !== true) {
          autoTools.scheduleFailedToolResponse(event.toolId, `Tool ${event.name} not found`)
        }
        break
      case 'rag-used':
        trackRagUsed(event, draft.conversationId, !draft.isExisting)
        break
    }
  }
}

/** Hand the turn to the engine, then apply the completion (or failure) side effects. */
export async function runEngineTurn(
  deps: SubmitFlowDeps,
  draft: TurnDraft,
  turn: TurnPresentation,
  state: SubmissionState,
  args: {
    chatRequestBody: ChatRequestBody
    attachedContext: Array<FinalizedAttachment>
    includedAttachments: Array<ChatAttachment>
    autoTools: AutoToolTracker
    onTurnAccepted?: () => void
  },
): Promise<void> {
  // The turn's current registry key: the presentation's side key until the
  // stream owns the chat, then adopted onto the (possibly renamed) chat id.
  const registryKey = { current: turn.registryKey }
  // Filled right after startTurn (no events can arrive before then) — lets the
  // observer settle the turn when its queued bubble turns out to be cancelled.
  const abortTurn: { current: (() => void) | null } = { current: null }
  const observer = createTurnObserver(
    deps,
    draft,
    turn,
    state,
    args.autoTools,
    registryKey,
    abortTurn,
    args.onTurnAccepted,
  )

  const seed = initialTurnState(draft.conversationId, {
    modelId: draft.modelId,
    userMessageId: draft.initialMessage.id,
    placeholderClientId: draft.assistantClientId,
    attachedContext: args.attachedContext,
  })

  const turnId = crypto.randomUUID()
  deps.engine.telemetry.trace({
    kind: 'submit:start',
    chatId: draft.conversationId,
    turnId,
    msgId: args.chatRequestBody.msgId,
    isNewChat: !draft.isExisting,
    hasPrompt: !!draft.chatInput,
    attachmentCount: args.attachedContext.length,
    queued: registryKey.current !== draft.conversationId,
  })

  const handle = startTurn(registryKey.current, async (signal, isOwner) => {
    const turnOutcome = await submitTurn(
      deps.engine,
      {
        chatId: draft.conversationId,
        turnId,
        reportContext: {
          isNewChat: !draft.isExisting,
          hasAttachments: args.includedAttachments.length > 0,
          messageLength: draft.chatInput?.length,
        },
        body: args.chatRequestBody,
        seed,
        onEvent: observer,
        canResume: () => turn.canResume,
        getResumeConversationId: () => draft.backendConversationId,
        reconcileMessages: deps.data.reconcileChatMessages,
        // Read from the mutable draft so that once `metadata` renames the
        // chat, the cursor lands under the real id a resume will look up.
        onLastEventId: (lastEventId) => setResumeCursor(draft.conversationId, lastEventId),
      },
      signal,
    )
    // Read ownership before the registry entry self-deletes on settle —
    // after `handle.done` resolves, `isOwner()` is false even for the
    // turn that ran to completion unchallenged.
    state.ownedTurnAtSettle = isOwner()
    return turnOutcome
  })
  abortTurn.current = () => handle.controller.abort('unsubscribed')

  const outcome = await handle.done
  if (outcome.isErr()) {
    deps.store.setPendingMessage((prev) => {
      if (!prev || prev.id !== draft.pendingMessageId) return prev
      const retryOptions = { ...prev.options }
      // A turn the agent itself failed is tombstoned, and a resend of that id
      // echoes the stored outcome rather than re-running it, so Retry needs a
      // fresh id. Every other failure can leave the row live on the backend:
      // reusing the id dedupes, minting a new one enqueues a second copy.
      const backendFailedTurn =
        outcome.error._tag === 'BackendTurnError' &&
        (outcome.error.kind === 'stream-error-event' || outcome.error.kind === 'unsuccessful-completion')
      if (!backendFailedTurn && args.chatRequestBody.msgId && draft.backendConversationId) {
        retryOptions.resendMsgId = args.chatRequestBody.msgId
        retryOptions.conversationId = draft.backendConversationId
        retryOptions.isNewChat = false
      } else {
        delete retryOptions.resendMsgId
      }
      return { ...prev, options: retryOptions }
    })
    handleTurnFailure(deps, outcome.error, {
      chatId: draft.conversationId,
      wasTurnCommitted: turn.committed,
      // A promptless continuation commits no user bubble — the role-fallback
      // stamp would land on the previous turn's user message.
      userStamp: { omit: !draft.chatInput },
    })
    return
  }

  const completion = outcome.value
  if (completion._tag !== 'completed') {
    // Interrupted (superseded) — the new owner drives all shared state.
    return
  }

  // A committed turn that produced no assistant events would leave the
  // optimistic placeholder behind as an id-less message forever — drop it.
  if (turn.committed && completion.state.placeholderOwnerId === undefined) {
    deps.store.removeUnadoptedPlaceholder(draft.conversationId, draft.assistantClientId)
  }

  // SSE stream closed cleanly. On the queue path this can also mean the
  // bubble was cancelled (DELETE → stream closes without `metadata`).
  // Turn-completion side effects only make sense when a turn committed.
  if (turn.committed) {
    // If more messages are queued for this chat, shimmer the next-to-drain
    // bubble until the next `metadata`.
    if (deps.queue.getForChat(draft.conversationId).length > 0) {
      deps.queue.markAwaitingNextDrain(draft.conversationId)
    }
    args.autoTools.onStreamCompleted()
  }

  // Clear pending message upon successful completion — but only if it
  // still belongs to this submission (a queued follow-up in another chat
  // may have replaced it while this stream was open).
  deps.store.setPendingMessage((prev) => (prev && prev.id === draft.pendingMessageId ? undefined : prev))

  if (turn.committed) {
    GlobalEvents.emit('chat:message:sent', {
      chatId: draft.conversationId,
      conversationId: draft.conversationId,
      userMessageId: draft.initialMessage.id,
    })
  }
}
